/**
 * 基础存储模块
 * 提供通用的CRUD操作方法
 */


type Response<T> = {
  data?: T
  code: 200 | 400 | 401 | 403 | 404 | 500
  message?: string
}

export abstract class BaseStore<T> {
  protected db: IDBDatabase | null = null
  protected storeName: string

  constructor(db: IDBDatabase | null, storeName: string) {
    this.db = db
    this.storeName = storeName
  }

  /**
   * 设置数据库连接
   */
  setDatabase(db: IDBDatabase): void {
    this.db = db
  }

  /**
   * 创建对象存储
   */
  createObjectStore(db: IDBDatabase) {
    if (!db) throw new Error('数据库未初始化')
    if (db.objectStoreNames.contains(this.storeName)) return
    db.createObjectStore(this.storeName, { keyPath: 'id' })
    // 创建索引
    // store.createIndex('name', 'name', { unique: true })
  }

  /**
   * 添加数据
   */
  protected async add(data: T): Promise<Response<void>> {
    if (!this.db) throw new Error('数据库未初始化')

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.storeName], 'readwrite')
      const store = transaction.objectStore(this.storeName)
      const request = store.add(data)

      request.onsuccess = () => resolve({ code: 200 })
      request.onerror = () => reject(request.error)
    })
  }

  /**
   * 更新数据
   */
  protected async update(data: T): Promise<Response<void>> {
    if (!this.db) throw new Error('数据库未初始化')

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.storeName], 'readwrite')
      const store = transaction.objectStore(this.storeName)
      const request = store.put(data)
      request.onsuccess = () => resolve({ code: 200 })
      request.onerror = () => reject({ code: 500, message: request.error?.message })
    })
  }

  /**
   * 删除数据
   */
  protected async delete(id: string): Promise<Response<void>> {
    if (!this.db) throw new Error('数据库未初始化')

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.storeName], 'readwrite')
      const store = transaction.objectStore(this.storeName)
      const request = store.delete(id)

      request.onsuccess = () => resolve({ code: 200 })
      request.onerror = () => reject({ code: 500, message: request.error?.message })
    })
  }

  /**
   * 根据ID获取数据
   */
  protected async get(id: string): Promise<Response<T | undefined>> {
    if (!this.db) throw new Error('数据库未初始化')

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.storeName], 'readonly')
      const store = transaction.objectStore(this.storeName)
      const request = store.get(id)

      request.onsuccess = () => resolve({ code: 200, data: request.result })
      request.onerror = () => reject({ code: 500, message: request.error?.message })
    })
  }

  /**
   * 获取所有数据
   */
  protected async getAll(): Promise<Response<T[]>> {
    if (!this.db) throw new Error('数据库未初始化')
    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.storeName], 'readonly')
      const store = transaction.objectStore(this.storeName)
      const request = store.getAll()

      request.onsuccess = () => resolve({ code: 200, data: request.result })
      request.onerror = () => reject({ code: 500, message: request.error?.message })
    })
  }

  /**
   * 根据索引获取数据
   */
  protected async getByIndex(indexName: string, value: any): Promise<Response<T>> {
    if (!this.db) throw new Error('数据库未初始化')

    return new Promise((resolve) => {
      const transaction = this.db!.transaction([this.storeName], 'readonly')
      const store = transaction.objectStore(this.storeName)
      const index = store.index(indexName)
      // 是否存在
      const request = index.get(value)
      request.onsuccess = () => resolve({ code: 200, data: request.result })
      request.onerror = () => resolve({ code: 500, message: request.error?.message })
    })
  }

  /**
   * 根据ID获取数据
   */
  protected async getById(id: string): Promise<Response<T>> {
    if (!this.db) throw new Error('数据库未初始化')
    return new Promise((resolve) => {
      const transaction = this.db!.transaction([this.storeName], 'readonly')
      const store = transaction.objectStore(this.storeName)
      const request = store.get(id)
      request.onsuccess = () => resolve({ code: 200, data: request.result })
      request.onerror = () => resolve({ code: 500, message: request.error?.message })
    })
  }

  /**
   * 清空存储
   */
  protected async clear(): Promise<Response<void>> {
    if (!this.db) throw new Error('数据库未初始化')

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.storeName], 'readwrite')
      const store = transaction.objectStore(this.storeName)
      const request = store.clear()

      request.onsuccess = () => resolve({ code: 200 })
      request.onerror = () => reject({ code: 500, message: request.error?.message })
    })
  }

  /**
   * 生成唯一ID
   */
  protected generateId(): string {
    return Date.now().toString(36) + Math.random().toString(36).substr(2)
  }

  // 公共方法，供外部访问
  /**
   * 获取所有数据（公共方法）
   */
  async getAllData(): Promise<Response<T[]>> {
    return this.getAll()
  }

  /**
   * 根据索引获取数据（公共方法）
   */
  async getDataByIndex(indexName: string, value: any): Promise<Response<T>> {
    return this.getByIndex(indexName, value)
  }

  /**
   * 根据ID获取数据（公共方法）
   */
  async getDataById(id: string): Promise<Response<T>> {
    return this.getById(id)
  }

  /**
   * 清空存储（公共方法）
   */
  async clearData(): Promise<Response<void>> {
    return this.clear()
  }

  /**
   * 添加数据（公共方法）
   */
  async addData(data: T): Promise<Response<void>> {
    return this.add(data)
  }

  /**
   * 更新数据（公共方法）
   */
  async updateData(data: T): Promise<Response<void>> {
    return this.update(data)
  }

  /**
   * 删除数据（公共方法）
   */
  async deleteData(id: string): Promise<Response<void>> {
    return this.delete(id)
  }
} 